import { ChangeEvent, FunctionComponent, useEffect, useRef, useState } from 'react'
import { css, cx } from '@emotion/css'
import { Theme, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import ReCAPTCHA from 'react-google-recaptcha'
import { useTranslation } from 'react-i18next'
import type { GiftCard, GiftCardRequest } from '@/libs/helpers/apiClient'
import { Button, CheckBox, Divider, formStyles, Grid, SvgIcon, Text } from '@/tbui'

const StyledButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 34px;
  width: 88px;
`

const ToolTipContent = styled.span`
  visibility: hidden;
  background-color: ${(props) => props.theme.palette.TEXT};
  color: ${(props) => props.theme.palette.WHITE};
  border-radius: 6px;
  position: absolute;
  z-index: 1;
  margin-top: 20px;
  left: 0;
  padding: 12px 20px;
  text-align: left;
  line-height: 20px;
  font-size: 13px;
  @media (max-width: ${(props) => props.theme.breakpoints.MD}) {
    width: 250px;
  }
  @media (min-width: ${(props) => props.theme.breakpoints.MD}) {
    width: 370px;
  }
`

const ToolTip = styled.div`
  position: absolute;
  display: inline-block;
  &:hover span {
    visibility: visible;
  }
`

const StyledLabel = styled.label<{ hasError: boolean }>`
  ${({ theme }) => formStyles(theme).label}
  display: block;
  ${(props) => props.hasError && { color: `${props.theme.palette.ERROR}` }}
`

const StyledGrid = styled(Grid)`
  align-items: top;
  ${(props) => `@media (max-width: ${props.theme.breakpoints.SM}) {
    display: contents;

    button {
      width: 100%;
      margin-top: 5px;
    }
  }
  `}
`

const giftCartInputStyles = (theme: Theme): string => css`
  ${formStyles(theme).input}

  .has-error {
    ${formStyles(theme).invalid}
  }
`

const StyledReCAPTCHA = styled(ReCAPTCHA)`
  z-index: 10;
`

interface DigitalCardProps {
  title: string
  isCartLoading?: boolean
  handleDGCChange: (e: ChangeEvent<HTMLInputElement>) => void
  giftCardOptions: GiftCard[]
  isGiftCardPicked: boolean
  selectedGiftCardCode?: string | null
  handleAddGiftCard: ({ code, recaptchaToken }: GiftCardRequest) => void
  applyGiftCardError: string | null
}

const GiftCardPicker: FunctionComponent<DigitalCardProps> = ({
  title,
  isCartLoading,
  handleDGCChange,
  giftCardOptions,
  isGiftCardPicked,
  selectedGiftCardCode,
  handleAddGiftCard,
  applyGiftCardError,
}) => {
  const noGiftCardOptions = giftCardOptions.length === 0
  // HOOKS
  const { t } = useTranslation()
  const theme = useTheme()
  // STATES
  const [code, setCode] = useState('')
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const hasError = applyGiftCardError != null
  const isInputDisabled = isGiftCardPicked || isCartLoading
  const isButtonDisabled = isInputDisabled || code.length < 1

  const checkOneOption = (e: ChangeEvent<HTMLInputElement>): void => {
    if (e && !isCartLoading) {
      e.persist()
      handleDGCChange(e)
    }
  }

  const onSubmit = async (): Promise<void> => {
    const recaptchaToken = await recaptchaRef.current?.executeAsync()
    if (recaptchaToken == null) {
      return
    }
    handleAddGiftCard({ code, recaptchaToken })
  }

  useEffect(() => {
    if (isGiftCardPicked) {
      setCode('')
    }
  }, [isGiftCardPicked])

  return (
    <div data-test="gift-card-picker">
      <Text type="H4" m={`36px 0 ${noGiftCardOptions ? '24px' : '12px'} 0`}>
        {title}
      </Text>
      {!noGiftCardOptions &&
        giftCardOptions
          .filter((giftCard) => giftCard.code !== '')
          .map((giftCard) => (
            <Grid key={giftCard.code}>
              <Grid
                template="auto 1fr"
                gap="16px"
                align="center"
                m="12px 0 15px 5px"
                data-test={`gift-card-${giftCard.code}`}
              >
                <CheckBox
                  onChange={checkOneOption}
                  isSelected={giftCard.code === selectedGiftCardCode}
                  id={giftCard.code}
                  variant="ADD"
                  isDisabled={isCartLoading}
                />
                <Grid gap="6px">
                  <Text m="0 0 5px 0">
                    {t('checkout.payment.option_gift_card', {
                      amount: giftCard.currentValue,
                    })}
                  </Text>
                </Grid>
              </Grid>
              <Divider />
            </Grid>
          ))}
      <Grid
        template="auto 1fr"
        gap="16px"
        m={!noGiftCardOptions ? '15px 0 5px 5px' : '0 0 5px 5px'}
      >
        <SvgIcon name="gift" fontSize="28px" color={isButtonDisabled ? 'GRAY_2' : 'PRIMARY'} />
        <Grid gap="6px">
          <StyledLabel htmlFor="gift-card-enter-code" hasError={hasError}>
            {t('checkout.payment.add_gift_card')}
            <ToolTip>
              <SvgIcon name="tooltip" color="PRIMARY" fontSize="16px" m="0 0 0 5px" />
              <ToolTipContent>{t('checkout.payment.gift_card_tooltip')}</ToolTipContent>
            </ToolTip>
          </StyledLabel>
          <StyledGrid gap="6px" template="auto 2fr" justify="left">
            <Grid>
              <input
                data-dd-privacy="mask"
                id="gift-card-enter-code"
                placeholder={t('checkout.payment.gift_card_enter_code')}
                disabled={isInputDisabled}
                onPaste={(e) => {
                  return setCode(e.clipboardData.getData('text').split(/\s+/).join(''))
                }}
                onChange={(e) => setCode(e.target.value.split(/\s+/).join(''))}
                value={code}
                maxLength={10}
                className={cx(giftCartInputStyles(theme), `${hasError ? 'has-error' : ''}`)}
                data-test="gift-card-input"
              />
              {hasError && (
                <Text color="ERROR" m="3px 0 0 0">
                  {applyGiftCardError}
                </Text>
              )}
            </Grid>

            <StyledButton
              data-test="add-gift-card"
              variant="OUTLINE"
              type="button"
              disabled={isButtonDisabled}
              onClick={onSubmit}
            >
              {t('checkout.payment.gift_card_add')}
            </StyledButton>
          </StyledGrid>
          <StyledReCAPTCHA
            sitekey={process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_KEY ?? ''}
            ref={recaptchaRef}
            size="invisible"
            onChange={() => {
              recaptchaRef.current?.reset()
            }}
          />
        </Grid>
      </Grid>
    </div>
  )
}

export default GiftCardPicker
